import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetLinksQuery,
  useGetLinksServicesQuery,
  useGetLinksOffersQuery,
  useGetLinksAnalyticsQuery,
  useCreateLinkMutation,
  useUpdateLinkMutation,
  useDeleteLinkMutation,
} from "../redux/services/api";
import { selectUser } from "../redux/slices/authSlice";
import Icon from "../components/Icon";
import { useTranslation } from "../hooks/useTranslation";

const Links = () => {
  const user = useSelector(selectUser);
  const { t } = useTranslation();

  // RTK Query hooks
  const { data: linksData, isLoading: loading } = useGetLinksQuery();
  const { data: servicesData } = useGetLinksServicesQuery();
  const { data: offersData } = useGetLinksOffersQuery();
  const { data: analyticsData } = useGetLinksAnalyticsQuery();

  const [createLink] = useCreateLinkMutation();
  const [updateLink] = useUpdateLinkMutation();
  const [deleteLink] = useDeleteLinkMutation();

  const links = linksData?.data || [];
  const services = servicesData?.data || [];
  const offers = offersData?.data || [];
  const analytics = analyticsData?.data || null;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [copyingLink, setCopyingLink] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "service",
    service_id: "",
    offer_id: "",
    custom_url: "",
    is_active: true,
  });

  const handleCreateLink = async (e) => {
    e.preventDefault();
    try {
      await createLink(formData).unwrap();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error("Error creating link:", error);
      alert(error.data?.message || "Error creating link");
    }
  };

  const handleEditLink = async (e) => {
    e.preventDefault();
    try {
      await updateLink({ id: editingLink.id, body: formData }).unwrap();
      setShowEditModal(false);
      setEditingLink(null);
      resetForm();
    } catch (error) {
      console.error("Error updating link:", error);
      alert(error.data?.message || "Error updating link");
    }
  };

  const handleDeleteLink = async (id) => {
    if (!window.confirm("Are you sure you want to delete this link?")) return;

    try {
      await deleteLink(id).unwrap();
    } catch (error) {
      console.error("Error deleting link:", error);
      alert(error.data?.message || "Error deleting link");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      type: "service",
      service_id: "",
      offer_id: "",
      custom_url: "",
      is_active: true,
    });
  };

  const openEditModal = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      description: link.description || "",
      type: link.type,
      service_id: link.service_id || "",
      offer_id: link.offer_id || "",
      custom_url: link.custom_url || "",
      is_active: link.is_active,
    });
    setShowEditModal(true);
  };

  const copyToClipboard = async (text, linkId) => {
    setCopyingLink(linkId);
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        showToast("Link copied to clipboard!", "success");
      } else {
        // Fallback for older browsers or non-HTTPS
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            showToast("Link copied to clipboard!", "success");
          } else {
            showToast(
              "Failed to copy link. Please copy manually: " + text,
              "error"
            );
          }
        } catch (err) {
          showToast(
            "Failed to copy link. Please copy manually: " + text,
            "error"
          );
        }

        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error("Failed to copy: ", err);
      showToast("Failed to copy link. Please copy manually: " + text, "error");
    } finally {
      setCopyingLink(null);
    }
  };

  const showToast = (message, type = "info") => {
    // Create toast element
    const toast = document.createElement("div");
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${
              type === "success"
                ? "#22c55e"
                : type === "error"
                ? "#ef4444"
                : "#3b82f6"
            };
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            z-index: 10000;
            font-size: 14px;
            font-weight: 500;
            max-width: 300px;
            word-wrap: break-word;
        `;

    document.body.appendChild(toast);

    // Remove toast after 3 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 3000);
  };

  const generateQRCode = (url) => {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
      url
    )}`;
    return qrUrl;
  };

  const getLinkUrl = (link) => {
    return `${window.location.origin}/l/${link.slug}`;
  };

  const getRedirectTarget = (link) => {
    switch (link.type) {
      case "service":
        return link.service ? link.service.title : "Service not found";
      case "offer":
        return link.offer ? link.offer.title : "Offer not found";
      case "custom_url":
        return link.custom_url;
      default:
        return "Unknown";
    }
  };

  if (loading) {
    return (
      <div className="dashboard-modern">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("linksDashboard.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="links">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <Icon name="link" className="header-icon" size="24px" />
            {t("linksDashboard.linkManagement")}
          </h1>
          <p>{t("linksDashboard.createAndTrackLinks")}</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="add-btn"
            onClick={() => setShowCreateModal(true)}
          >
            <Icon name="plus" size="16px" />
            {t("linksDashboard.createNewLink")}
          </button>
        </div>
      </div>

      {/* Analytics Section */}
      {analytics && (
        <div className="links-analytics">
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="analytics-icon">
                <Icon name="link" size="20px" />
              </div>
              <div className="analytics-content">
                <h3>{t("linksDashboard.totalLinks")}</h3>
                <p className="analytics-number">{analytics.total_links}</p>
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-icon">
                <Icon name="check-circle" size="20px" />
              </div>
              <div className="analytics-content">
                <h3>{t("linksDashboard.activeLinks")}</h3>
                <p className="analytics-number">{analytics.active_links}</p>
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-icon">
                <Icon name="eye" size="20px" />
              </div>
              <div className="analytics-content">
                <h3>{t("linksDashboard.totalClicks")}</h3>
                <p className="analytics-number">{analytics.total_clicks}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="links-container">
        {links.length > 0 ? (
          <div className="links-table-container">
            <table className="links-table">
              <thead>
                <tr>
                  <th>{t("linksDashboard.title")}</th>
                  <th>{t("linksDashboard.type")}</th>
                  <th>{t("linksDashboard.target")}</th>
                  <th>{t("linksDashboard.shortUrl")}</th>
                  <th>{t("linksDashboard.clicks")}</th>
                  <th>{t("linksDashboard.status")}</th>
                  <th>{t("linksDashboard.created")}</th>
                  <th>{t("linksDashboard.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {links.map((link) => (
                  <tr key={link.id} className="link-row">
                    <td className="title-cell">
                      <div className="link-title-info">
                        <h4>{link.title}</h4>
                        {link.description && (
                          <p className="link-description">{link.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="type-cell">
                      <span className="type-badge">
                        {link.type.replace("_", " ")}
                      </span>
                    </td>
                    <td className="target-cell">
                      <span className="target-info">
                        {getRedirectTarget(link)}
                      </span>
                    </td>
                    <td className="url-cell">
                      <div className="url-container">
                        <input
                          type="text"
                          value={getLinkUrl(link)}
                          readOnly
                          className="url-input"
                        />
                        <button
                          className="copy-btn"
                          onClick={() =>
                            copyToClipboard(getLinkUrl(link), link.id)
                          }
                          title={t("linksDashboard.copyLink")}
                          disabled={copyingLink === link.id}
                        >
                          <Icon name="copy" size="14px" />
                        </button>
                      </div>
                    </td>
                    <td className="clicks-cell">
                      <span className="clicks-count">{link.click_count}</span>
                    </td>
                    <td className="status-cell">
                      <span
                        className={`status-badge ${
                          link.is_active ? "active" : "inactive"
                        }`}
                      >
                        {link.is_active
                          ? t("linksDashboard.active")
                          : t("linksDashboard.inactive")}
                      </span>
                    </td>
                    <td className="date-cell">
                      {new Date(link.created_at).toLocaleDateString()}
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="qr-btn"
                          onClick={() =>
                            window.open(
                              generateQRCode(getLinkUrl(link)),
                              "_blank"
                            )
                          }
                          title={t("linksDashboard.viewQRCode")}
                        >
                          <Icon name="qrcode" size="14px" />
                        </button>
                        <button
                          className="edit-btn"
                          onClick={() => openEditModal(link)}
                          title={t("linksDashboard.editLink")}
                        >
                          <Icon name="edit" size="14px" />
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteLink(link.id)}
                          title={t("linksDashboard.deleteLink")}
                        >
                          <Icon name="trash" size="14px" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-links">
            <div className="empty-content">
              <Icon name="link" size="48px" />
              <h3>{t("linksDashboard.noLinksYet")}</h3>
              <p>{t("linksDashboard.getStartedByCreating")}</p>
              <button
                className="add-first-link-btn"
                onClick={() => setShowCreateModal(true)}
              >
                <Icon name="plus" size="16px" />
                Create First Link
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Link Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{t("linksDashboard.createNewLink")}</h2>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                <Icon name="x" size="20px" />
              </button>
            </div>
            <form onSubmit={handleCreateLink} className="modal-body">
              <div className="form-group">
                <label>{t("linksDashboard.title")} *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      title: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>{t("linksDashboard.description")}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>{t("linksDashboard.linkType")} *</label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type: e.target.value,
                    })
                  }
                  required
                >
                  <option value="service">{t("linksDashboard.service")}</option>
                  <option value="offer">{t("linksDashboard.offer")}</option>
                  <option value="custom_url">
                    {t("linksDashboard.custom")}
                  </option>
                </select>
              </div>

              {formData.type === "service" && (
                <div className="form-group">
                  <label>{t("linksDashboard.service")} *</label>
                  <select
                    value={formData.service_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        service_id: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">
                      {t("linksDashboard.selectService")} ({services.length}{" "}
                      available)
                    </option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.type === "offer" && (
                <div className="form-group">
                  <label>{t("linksDashboard.offer")} *</label>
                  <select
                    value={formData.offer_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        offer_id: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">
                      {t("linksDashboard.selectOffer")} ({offers.length}{" "}
                      available)
                    </option>
                    {offers.map((offer) => (
                      <option key={offer.id} value={offer.id}>
                        {offer.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.type === "custom_url" && (
                <div className="form-group">
                  <label>{t("linksDashboard.customUrl")} *</label>
                  <input
                    type="url"
                    value={formData.custom_url}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        custom_url: e.target.value,
                      })
                    }
                    placeholder="https://example.com"
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_active: e.target.checked,
                      })
                    }
                  />
                  {t("linksDashboard.active")}
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  {t("linksDashboard.cancel")}
                </button>
                <button type="submit" className="btn-primary">
                  {t("linksDashboard.createLink")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Link Modal */}
      {showEditModal && editingLink && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Link</h2>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingLink(null);
                  resetForm();
                }}
              >
                <Icon name="x" size="20px" />
              </button>
            </div>
            <form onSubmit={handleEditLink} className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      title: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>{t("linksDashboard.description")}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>{t("linksDashboard.linkType")} *</label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type: e.target.value,
                    })
                  }
                  required
                >
                  <option value="service">{t("linksDashboard.service")}</option>
                  <option value="offer">{t("linksDashboard.offer")}</option>
                  <option value="custom_url">
                    {t("linksDashboard.custom")}
                  </option>
                </select>
              </div>

              {formData.type === "service" && (
                <div className="form-group">
                  <label>{t("linksDashboard.service")} *</label>
                  <select
                    value={formData.service_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        service_id: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">
                      {t("linksDashboard.selectService")} ({services.length}{" "}
                      available)
                    </option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.type === "offer" && (
                <div className="form-group">
                  <label>{t("linksDashboard.offer")} *</label>
                  <select
                    value={formData.offer_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        offer_id: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">
                      {t("linksDashboard.selectOffer")} ({offers.length}{" "}
                      available)
                    </option>
                    {offers.map((offer) => (
                      <option key={offer.id} value={offer.id}>
                        {offer.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.type === "custom_url" && (
                <div className="form-group">
                  <label>{t("linksDashboard.customUrl")} *</label>
                  <input
                    type="url"
                    value={formData.custom_url}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        custom_url: e.target.value,
                      })
                    }
                    placeholder="https://example.com"
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_active: e.target.checked,
                      })
                    }
                  />
                  {t("linksDashboard.active")}
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingLink(null);
                    resetForm();
                  }}
                >
                  {t("linksDashboard.cancel")}
                </button>
                <button type="submit" className="btn-primary">
                  Update Link
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Links;
